import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import debug from 'util/debug'
import { useDispatch, useSelector } from 'react-redux'
import { selectIsAddingNote } from 'selectors/page'
import {
  doRemoveDraftRecipients,
  doSearchRecipients,
  doUpdateReplyOrForwardDraftToRecipientsLocally,
} from 'ducks/drafts/operations'

import {
  selectDraftById,
  selectDraftIdByTicketId,
} from 'ducks/drafts2/selectors'
import { doChangeDraft } from 'ducks/drafts2/operations'

import { emptyArr } from 'util/arrays'
import { selectChangesetsForTicketId } from 'selectors/currentChangesets'
import { buildDraftDefaults } from 'ducks/drafts2/util'
import { selectRawTicketById } from 'selectors/tickets'

export const useRecipientsEditor = ({
  type,
  ticketId,
  draftType = 'reply',
  allowMultipleRecipients = false,
  startInEditMode = false,
}) => {
  const searchBoxRef = useRef(null)
  const [editing, setIsEditing] = useState(startInEditMode)
  const [query, setQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchedRecipients, setSearchedRecipients] = useState([])
  const draftId = useSelector(state =>
    selectDraftIdByTicketId(state, ticketId, draftType)
  )
  const ticketChangesets = useSelector(state =>
    selectChangesetsForTicketId(state, ticketId)
  )
  const ticket = useSelector(state => selectRawTicketById(state, ticketId))
  const ticketsState = useSelector(state => state.tickets)

  const defaultDraft = useMemo(
    () => buildDraftDefaults(ticketsState, ticket, ticketChangesets),
    [ticketsState, ticket, ticketChangesets]
  )

  const stateDraft = useSelector(state => selectDraftById(state, draftId))

  const draft = stateDraft || defaultDraft

  const dispatch = useDispatch()
  const forNote = useSelector(selectIsAddingNote)
  const allDraftRecipients = useMemo(
    () => {
      return [
        ...(draft.to || emptyArr),
        ...(draft.cc || emptyArr),
        ...(draft.bcc || emptyArr),
      ]
    },
    [draft]
  )
  const recipients = useMemo(() => draft[type] || emptyArr, [draft, type])

  const removeDraftRecipients = useCallback(
    (inputTicketId, inputRecipients) => {
      dispatch(doRemoveDraftRecipients(inputTicketId, inputRecipients))
    },
    [dispatch]
  )

  const startEditing = useCallback(
    () => {
      if (allowMultipleRecipients || recipients.length === 0) {
        setIsEditing(true)
      }
    },
    [allowMultipleRecipients, recipients]
  )

  const stopEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const onEscape = useCallback(
    () => {
      setQuery('')
      stopEditing()
    },
    [stopEditing]
  )

  const onBulkAdd = useCallback(
    inputRecipients => {
      const recipientsToAdd = inputRecipients.filter(
        recipient =>
          !recipients.some(
            existingRecipient => recipient.email === existingRecipient.email
          )
      )

      if (recipientsToAdd.length > 0) {
        const newRecipients = allowMultipleRecipients
          ? [].concat(...recipients, ...recipientsToAdd)
          : [recipientsToAdd[0]]

        // HACK Kevin R (2023-10-06)
        // Ported this code from the original onUpdate functions. We should get rid
        // of it as part of deletion project for drafts v1
        //
        // Update the to field in state.drafts.replies,
        // so the contact related variables from canned replies can be converted correctly
        // if the to exists
        if (type === 'to') {
          dispatch(
            doUpdateReplyOrForwardDraftToRecipientsLocally(
              ticketId,
              newRecipients
            )
          )
        }

        dispatch(
          doChangeDraft(draftId, draftType, ticketId, null, {
            [type]: newRecipients,
          })
        )
      }
      setQuery('')
      if (!allowMultipleRecipients) {
        stopEditing()
      }
    },
    [
      ticketId,
      dispatch,
      type,
      recipients,
      draftId,
      draftType,
      allowMultipleRecipients,
      stopEditing,
    ]
  )

  const onAdd = useCallback(
    recipient => {
      onBulkAdd([recipient])
    },
    [onBulkAdd]
  )

  const onRemove = useCallback(
    recipient => {
      const exists = recipients.find(existingRecipient => {
        return recipient.email === existingRecipient.email
      })

      if (exists) {
        const index = recipients.indexOf(recipient)
        const newRecipients = [].concat(recipients)
        newRecipients.splice(index, 1)

        // HACK Kevin R (2023-10-06)
        // Ported this code from the original onUpdate functions. We should get rid
        // of it as part of deletion project for drafts v1
        //
        // Update the to field in state.drafts.replies,
        // so the contact related variables from canned replies can be converted correctly
        // if the to exists
        if (type === 'to') {
          dispatch(
            doUpdateReplyOrForwardDraftToRecipientsLocally(
              ticketId,
              newRecipients
            )
          )
        }
        dispatch(
          doChangeDraft(draftId, draftType, ticketId, null, {
            [type]: newRecipients,
          })
        )
      }
      setQuery('')
      if (!allowMultipleRecipients) {
        setIsEditing(true)
      }
    },
    [
      dispatch,
      ticketId,
      type,
      recipients,
      draftId,
      draftType,
      allowMultipleRecipients,
    ]
  )

  const searchRecipientsNow = useCallback(
    async newQuery => {
      try {
        setQuery(newQuery)
        const response = await dispatch(doSearchRecipients(type, newQuery))
        const contactsExcludingSelected = (response?.contacts || []).filter(
          c => !allDraftRecipients.some(rec => c.email === rec.email)
        )
        setSearchedRecipients(contactsExcludingSelected)
      } catch (error) {
        debug('Error occured while executing doBasicSearchContacts', { error })
      } finally {
        setIsSearching(false)
      }
    },
    [dispatch, setIsSearching, type, allDraftRecipients]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [searchRecipients] = useDebouncedCallback(searchRecipientsNow, 200)

  const onFocus = useCallback(
    e => {
      startEditing(e)
    },
    [startEditing]
  )

  const onBlur = useCallback(
    () => {
      if (query === '') stopEditing()
    },
    [stopEditing, query]
  )

  useEffect(
    () => {
      if (query === '') setSearchedRecipients([])
    },
    [query]
  )

  return {
    forNote,
    removeDraftRecipients,
    filtered: searchedRecipients,
    isSearching,
    recipients,
    onEscape,
    onAdd,
    onBulkAdd,
    onRemove,
    searchRecipientsNow,
    searchRecipients,
    allowMultipleRecipients,
    onFocus,
    onBlur,
    query,
    editing,
    startEditing,
    stopEditing,
    searchBoxRef,
  }
}
